import { motion } from "motion/react";
import styled from "styled-components";

import { DATA_E2E_KEY, IStyledAttributes } from "@ryde/shared";
import { blueGreyMui, blueMui, greyMui, ScreenDepth } from "@ryde/theme";

import { MAP_LEGEND_COLLAPSED_SIZE_PX } from "./collapsible-map-legend.constants";

/**
 * A map key overlay pinned to a Leaflet map's top-left, rendered as a motion element so it can crossfade
 * to/from its collapsed button. The panel body is non-interactive (pointer-events: none) so it never
 * intercepts map panning; only the collapse control opts back in.
 */
export const MapLegendPanelStyled = styled(motion.div).attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "map-legend",
  },
)`
  position: absolute;
  top: 10px;
  left: 60px /* @noflip */;
  z-index: ${ScreenDepth.MAP_TRACKING_TRAIL_LEGEND};
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 14px;
  border-radius: 12px;
  background-color: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(6px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.18);
  border: 1px solid ${greyMui._200};
  pointer-events: none;
  transform-origin: top left /* @noflip */;

  .map-legend__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    margin-bottom: 2px;
  }

  .map-legend__title {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${greyMui._500};

    svg {
      font-size: 15px;
      color: ${greyMui._500};
    }
  }

  /* The one interactive part of the panel — re-enable pointer events just for the collapse control. */
  .map-legend__collapse {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 2px;
    border: none;
    border-radius: 6px;
    background: transparent;
    color: ${greyMui._500};
    cursor: pointer;
    pointer-events: auto;
    transition:
      background 0.15s ease,
      color 0.15s ease;

    svg {
      font-size: 16px;
    }

    &:hover {
      background: ${greyMui._100};
      color: ${blueGreyMui._800};
    }
  }

  .map-legend__row {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: ${greyMui._800};
  }
`;

/** The compact round button the legend collapses into; clicking it restores the full key. */
export const MapLegendCollapsedButtonStyled = styled(
  motion.button,
).attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "map-legend-toggle",
})`
  position: absolute;
  top: 10px;
  left: 60px /* @noflip */;
  z-index: ${ScreenDepth.MAP_TRACKING_TRAIL_LEGEND};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${MAP_LEGEND_COLLAPSED_SIZE_PX}px;
  height: ${MAP_LEGEND_COLLAPSED_SIZE_PX}px;
  padding: 0;
  border: 1px solid ${greyMui._200};
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(6px);
  color: ${blueGreyMui._700};
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.18);
  cursor: pointer;
  pointer-events: auto;
  transform-origin: top left /* @noflip */;
  transition:
    color 0.15s ease,
    box-shadow 0.15s ease;

  svg {
    font-size: 20px;
  }

  &:hover {
    color: ${blueMui._700};
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.24);
  }
`;

/** A short line sample in a legend row's color and style — `$lineStyle` is a CSS `border-top-style` value. */
export const MapLegendSwatchStyled = styled.span.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "map-legend-swatch",
})<{ $color: string; $lineStyle: string }>`
  flex-shrink: 0;
  width: 22px;
  height: 0;
  border-top-width: 3px;
  border-top-style: ${({ $lineStyle }) => $lineStyle};
  border-top-color: ${({ $color }) => $color};
`;
